import React, {useEffect, useState} from 'react';
import {
  ApiProblemDetails,
  ApiPermissionBasicOutputDto,
  ApiPermissionGroupInputDto,
  ApiPermissionGroupOutputDto,
} from '../../../types/api';
import {
  ContextPanel,
  ContextPanelContentSection,
  FormikValmetTextInput,
  FormCancelConfirmDialog,
  FormikValmetComplexMultiSelect,
  errorNotification,
  LoadingScreen,
} from '@valmet-iop/ui-common';
import {Formik} from 'formik';
import {apiGetRequest, apiPostRequest} from '../../../services/api/apiService';
import * as Yup from 'yup';
import {useTranslation} from 'react-i18next';

const createPermissionGroup = async (model: ApiPermissionGroupInputDto) => {
  const result = await apiPostRequest<
    ApiPermissionGroupInputDto,
    ApiPermissionGroupOutputDto
  >('api/permissionGroups', model);
  return result;
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('permissionGroups:create.errors.nameRequired')
    .max(128, 'permissionGroups:create.errors.nameMaxLength'),
  description: Yup.string().max(
    256,
    'permissionGroups:create.errors.descriptionMaxLength',
  ),
});

const defaultValues: {
  name: string;
  description: string;
  selectedPermissions: {text: string; value: string}[];
} = {
  name: '',
  description: '',
  selectedPermissions: [],
};

const emptyOptions: {
  text: string;
  value: string;
}[] = [];

const CreateSidebar = (props: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (model: ApiPermissionGroupOutputDto) => void;
}) => {
  const {t} = useTranslation(['translation', 'permissionGroups']);
  const [isCancelConfirmDialogOpen, setCancelConfirmDialogOpen] = useState(
    false,
  );

  const [permissionOptions, setPermissionOptions] = useState(emptyOptions);
  const [isLoading, setIsLoading] = useState(false);

  const isOpen = props.isOpen;

  useEffect(() => {
    if (isOpen) {
      const getPermissions = async () => {
        setIsLoading(true);
        try {
          const result = await apiGetRequest<ApiPermissionBasicOutputDto[]>(
            'api/permissions',
          );
          setPermissionOptions(
            result.map(dto => ({
              text: dto.displayName,
              value: dto.permissionId,
            })),
          );
        } catch {
          errorNotification(
            t('permissionGroups:create.errors.lookupPermissionsFailed'),
          );
        } finally {
          setIsLoading(false);
        }
      };

      getPermissions();
    }
  }, [isOpen, t]);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={async (values, {setSubmitting, setFieldError, resetForm}) => {
        setSubmitting(true);
        try {
          const result = await createPermissionGroup({
            groupName: values.name,
            description: values.description,
            permissions: values.selectedPermissions.map(g => g.value),
          });
          props.onCreate(result);
          resetForm();
        } catch (e) {
          errorNotification(t('permissionGroups:create.errors.createFailed'));
          if ((e as ApiProblemDetails)?.status === 409) {
            setFieldError('name', 'permissionGroups:create.errors.conflict');
          }
        } finally {
          setSubmitting(false);
        }
      }}
      validationSchema={validationSchema}
    >
      {({isSubmitting, values, errors, dirty, handleSubmit, resetForm}) => {
        const onCloseClick = () => {
          if (dirty && !isSubmitting) {
            setCancelConfirmDialogOpen(true);
            return;
          }

          resetForm();
          props.onClose();
        };
        const onCancelConfirmDialogResult = (confirmed: boolean) => {
          setCancelConfirmDialogOpen(false);
          if (confirmed) {
            resetForm();
            props.onClose();
          }
        };
        return (
          <>
            <ContextPanel
              open={props.isOpen}
              size="small"
              titleTranslationKey="permissionGroups:create.title"
              bottomActions={[
                {
                  translationKey: 'permissionGroups:create.buttons.save',
                  style: 'primary',
                  isDisabled:
                    isSubmitting ||
                    Object.keys(errors).length > 0 ||
                    values === defaultValues,
                  onClick: handleSubmit,
                },
                {
                  translationKey: 'permissionGroups:create.buttons.cancel',
                  style: 'ghost',
                  isDisabled: isSubmitting,
                  onClick: onCloseClick,
                },
              ]}
              onCloseClick={onCloseClick}
            >
              {isSubmitting || isLoading ? (
                <LoadingScreen />
              ) : (
                <>
                  <ContextPanelContentSection headerTranslationKey="permissionGroups:create.general.header">
                    <FormikValmetTextInput
                      formProperty="name"
                      id="name"
                      labelTranslationKey="permissionGroups:create.general.name"
                      isRequired
                    />
                    <FormikValmetTextInput
                      formProperty="description"
                      id="description"
                      labelTranslationKey="permissionGroups:create.general.description"
                    />
                  </ContextPanelContentSection>
                  <ContextPanelContentSection headerTranslationKey="permissionGroups:create.permissions.header">
                    <FormikValmetComplexMultiSelect
                      formProperty="selectedPermissions"
                      options={permissionOptions}
                      selectPlaceholderTranslationKey="permissionGroups:create.permissions.addNewPlaceholder"
                    />
                  </ContextPanelContentSection>
                </>
              )}
            </ContextPanel>
            <FormCancelConfirmDialog
              isOpen={isCancelConfirmDialogOpen}
              onResult={onCancelConfirmDialogResult}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default CreateSidebar;
